import React, { useState } from 'react';
import {
  Box,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
  useTheme
} from '@mui/material';
import { useTheme as useCustomTheme } from '../theme/ThemeProvider';

const Login = ({ onLogin, apiClient }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();

  // Input validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    // In demo mode, accept any non-empty password
    return password.length > 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Sanitize input to prevent XSS
    // eslint-disable-next-line no-useless-escape
    const sanitizedValue = value.replace(/[<>\"'&]/g, '');
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Client-side validation
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    if (!validatePassword(formData.password)) {
      setError('Password cannot be empty.');
      setLoading(false);
      return;
    }

    try {
      // Use secure API client for login
      const user = await apiClient.login(formData.email, formData.password);
      onLogin(user);
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific error types
      if (error.message.includes('Invalid email or password')) {
        setError('Invalid email or password. Please try again.');
      } else if (error.message.includes('Account is deactivated')) {
        setError('Your account has been deactivated. Please contact support.');
      } else if (error.message.includes('Network')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDarkMode 
          ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: isDarkMode
            ? 'radial-gradient(circle at 20% 20%, rgba(67, 230, 194, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(144, 202, 249, 0.05) 0%, transparent 50%)'
            : 'radial-gradient(circle at 20% 20%, rgba(20, 184, 166, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(30, 42, 120, 0.03) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            position: 'relative',
            zIndex: 1
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3
            }}
          >
                            <img
                  src="/logo.png"
                  alt="Stratovai Logo"
                  style={{ 
                    height: '3rem', 
                    width: 'auto',
                    filter: isDarkMode ? 'drop-shadow(0 2px 4px rgba(255,255,255,0.1))' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                  }}
                />
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                background: isDarkMode 
                  ? 'linear-gradient(135deg, #90caf9 0%, #42a5f5 100%)'
                  : 'linear-gradient(135deg, #0099F7 0%, #0052D4 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textTransform: 'uppercase',
                letterSpacing: '-0.02em',
                textShadow: isDarkMode ? '0 2px 4px rgba(144,202,249,0.3)' : '0 2px 4px rgba(0,153,247,0.3)'
              }}
            >
              Stratovai
            </Typography>
          </Box>

          {/* Login Card */}
          <Paper
            elevation={8}
            sx={{
              width: '100%',
              maxWidth: 400,
              borderRadius: 3,
              overflow: 'hidden',
              background: theme.palette.background.paper,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h5"
                component="h2"
                align="center"
                gutterBottom
                sx={{ fontWeight: 600, mb: 1 }}
              >
                Welcome
              </Typography>
              
              <Typography
                variant="body2"
                align="center"
                sx={{ 
                  color: 'text.secondary', 
                  mb: 3,
                  fontWeight: 500
                }}
              >
                Intelligent QA & Product Platform
              </Typography>

              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Email Field */}
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    inputProps={{ 'data-test': 'login-username' }}
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />

                  {/* Password Field */}
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    inputProps={{ 'data-test': 'login-password' }}
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />

                  {/* Error Message */}
                  {error && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    data-test="login-submit"
                    sx={{
                      mt: 2,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #0099F7 0%, #0052D4 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0052D4 0%, #003d82 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 8px 25px rgba(0, 153, 247, 0.3)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>
                </Box>
              </form>


            </CardContent>
          </Paper>

          {/* Version badge */}
          <Box sx={{ position: 'fixed', bottom: 12, right: 16, opacity: 0.8 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1.25, py: 0.5, borderRadius: 1, border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.paper }}>
              {process.env.REACT_APP_BUILD_VERSION || 'v0.6.0-ui-explorer-scenarios'}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Login; 